import { useQuery } from '@apollo/client';
import Button from '@atlaskit/button';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tooltip from '@atlaskit/tooltip';
import {
  CreditCard,
  generatedTypesNew,
  Maybe,
  PaymentMethod,
  PaymentMethodType,
  Refunds,
  SortDirection,
} from '@css-xp/graphtypes';
import { PaymentMethods } from '@atlassian/cssxp-types-billing';
import { NEW_DATE_FORMAT, NEW_DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
import { getIdentifier } from '@atlassian/cssxp-utils-invoice';
import {
  ColGroup,
  commonTabs,
  DismissableInlineMessage,
  FormattedAddress,
  FormattedDate,
  formattedV2,
  HeadCell,
  Icon,
  InvoiceUtil,
  NoDataContentInTable,
  OpenSidebarRow,
  PageTabs,
  SearchResultColumn,
  TableBody,
  TableHead,
  useSidebar,
} from '@css-xp/base';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import kebabCase from 'lodash/kebabCase';
import memoize from 'lodash/memoize';
import React, { useCallback, useEffect, useState } from 'react';
import { fetchInvoicesForTransactionAccount } from './queries/fetch-invoices-for-transaction-account';
import { fetchPendingInvoicesForTransactionAccount } from './queries/fetch-pending-invoices-for-transaction-account';

import {
  FetchInvoicesForTransactionAccountQueryVariables,
  FetchInvoicesForTransactionAccountV2Query,
  FetchInvoicesForTransactionAccountV2QueryVariables,
  Invoice,
  InvoiceBillAndShipTo,
  TransactionAccount,
} from '@css-xp/graphtypes/src/generated-types-new';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { cloneDeep, defaultTo, set } from 'lodash';
import isNil from 'lodash/isNil';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { InvoiceChildRefundRow } from '../../../invoices/src/components/invoice-child-refund-row';
import './invoice-tab-transaction-account.scss';
import TransactionAccountInvoiceTable from './transaction-account-invoice-table';
export const PAGE_TAB_SEARCH_RESULTS_LOAD_MORE = 15;

interface Props {
  id: string;
  label?: string;
  transactionAccountId?: string;
  availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
  page?: string;
}

type ColumnList = HeadCell & SearchResultColumn;

export const getIsFreeInvoice = memoize((paymentMethod?: PaymentMethods | null) => {
  return paymentMethod === generatedTypesNew.InvoicePaymentMethod.NOPAYMENT;
});

const getSoldToAddress = (shipTo: InvoiceBillAndShipTo | undefined | null) => {
  if (!shipTo?.address) {
    return 'No address information';
  } else {
    return (
      <span className="line-clamp-sold-to-address">
        <FormattedAddress
          addressLine1={shipTo.address?.address1}
          addressLine2={shipTo.address?.address2}
          city={shipTo.address?.city}
          country={shipTo.address?.country}
          postcode={shipTo.address?.postalCode}
          state={shipTo.address?.state}
          organizationName={shipTo?.fullName}
        />
      </span>
    );
  }
};

const getDueOnCell = (cellName: string) => {
  return (
    <th>
      <Tooltip content={'Overdues in open invoices will be marked in red'} tag="span">
        <InfoIcon label="Info" size="small" />
      </Tooltip>
      <span className="due-on-text">{cellName}</span>
    </th>
  );
};

const getInnerTabValue = (searchUrl?: string) => {
  const params = new URLSearchParams(searchUrl || window.location.search);
  return defaultTo(params?.get('inner_tab'), 'ccp-invoices');
};

const HEAD = [
  {
    name: 'Invoice number',
    content: 'Invoice number',
  },
  {
    name: 'Sold-to address',
    content: 'Sold-to address',
    ccpOnly: true,
  },
  {
    name: 'Contact address',
    content: 'Contact address',
    hamsOnly: true,
  },
  {
    name: 'Payment method',
    content: 'Payment method',
  },
  {
    name: 'Due on',
    content: 'Due on',
    renderComponent: getDueOnCell('Due on'),
    isMandatory: false,
    isChecked: true,
  },
  {
    name: 'Issued on',
    content: 'Issued on',
    isMandatory: false,
    isChecked: true,
  },
  {
    name: 'PO number',
    content: 'PO number',
    isMandatory: false,
    isChecked: true,
    ccpOnly: true,
  },
  {
    name: 'Paid date',
    content: 'Paid date',
    isMandatory: false,
    isChecked: true,
    hamsOnly: true,
  },
  {
    name: 'Amount',
    content: 'Amount',
    isMandatory: false,
    isChecked: true,
  },
];

export const defaultSearchOptions: FetchInvoicesForTransactionAccountQueryVariables['searchOptions'] = {
  first: PAGE_TAB_SEARCH_RESULTS_LOAD_MORE,
  sort: {
    sortFields: ['createdDate', 'id'],
    sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
  },
};

const getFilteredColumns = (currentTab: string) =>
  HEAD.filter((column) => {
    if (currentTab === 'hams-invoices') return !column.ccpOnly;
    return !column.hamsOnly;
  });

const getInitialColumns = (location: any) => {
  return getFilteredColumns(getInnerTabValue(location.search));
};

const InvoiceTabTxA = React.memo(({ id, transactionAccountId, availablePaymentMethods, page = '' }: Props) => {
  const history = useHistory();
  const location = useLocation();
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [columnsList, setColumnList] = useState<Array<ColumnList>>(getInitialColumns(location));
  const [selectedTabKey, setSelectedTabKey] = useState<string>(getInnerTabValue(location.search));
  const [invoiceData, setInvoiceData] = useState<{ transactionAccount: TransactionAccount }>({
    transactionAccount: ([] as unknown) as TransactionAccount,
  });
  const { isSidebarOpen } = useSidebar();
  const { loading, error, data, refetch } = useQuery<
    FetchInvoicesForTransactionAccountV2Query,
    FetchInvoicesForTransactionAccountV2QueryVariables
  >(fetchInvoicesForTransactionAccount, {
    variables: {
      id: id,
      searchOptions: {
        ...defaultSearchOptions,
        first: rowsPerPage,
        offset: 0,
      },
    },
    displayName: 'fetchInvoicesForTransactionAccount',
    notifyOnNetworkStatusChange: true,
  });

  const lastEvaluatedKey: Maybe<string> | null = data?.transactionAccount?.hamsInvoices?.lastEvaluatedKey;
  const { data: pendingInvoiceData } = useQuery<
    generatedTypesNew.FetchPendingInvoicesForTransactionAccountQuery,
    generatedTypesNew.FetchPendingInvoicesForTransactionAccountQueryVariables
  >(fetchPendingInvoicesForTransactionAccount, {
    variables: {
      id: id,
    },
    displayName: 'fetchPendingInvoicesForTransactionAccount',
  });

  const updateData = (
    data: FetchInvoicesForTransactionAccountV2Query,
    invoiceData: { transactionAccount: TransactionAccount }
  ) => {
    // new hams data will be fetched from gql query ,and invoice data is the local state to which the newly fetched data from gql query will be appended
    const invoiceDataCopy = invoiceData?.transactionAccount?.hamsInvoices ? cloneDeep(invoiceData) : data;
    const newHamsData = data?.transactionAccount?.hamsInvoices?.nodes || [];
    const newCcpData = data?.transactionAccount?.invoices?.nodes || [];
    const prevHamsData = invoiceData?.transactionAccount?.hamsInvoices?.nodes || [];
    set(invoiceDataCopy, 'transactionAccount.hamsInvoices.nodes', [...prevHamsData, ...newHamsData]);
    set(invoiceDataCopy, 'transactionAccount.invoices.nodes', [...newCcpData]);
    return invoiceDataCopy;
  };

  useEffect(() => {
    if (data) {
      setInvoiceData(updateData(data, invoiceData));
    }
  }, [data]);

  const handleLoadMore = () => {
    refetch({
      searchOptions: {
        ...defaultSearchOptions,
        cursor: lastEvaluatedKey,
      },
    });
  };
  //variables to use for paging and populating our template
  const nodes =
    selectedTabKey === 'hams-invoices'
      ? invoiceData?.transactionAccount?.hamsInvoices?.nodes
      : invoiceData?.transactionAccount?.invoices?.nodes;
  const totalCount =
    selectedTabKey === 'hams-invoices'
      ? invoiceData?.transactionAccount?.hamsInvoices?.nodes?.length ?? 0
      : invoiceData?.transactionAccount?.invoices?.totalCount ?? 0;
  const hasNoResults = totalCount === 0;
  const onPageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchOptions: {
          ...defaultSearchOptions,
          first: rowsPerPage,
          offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
        },
        // filterOptions,
      });
    },
    [rowsPerPage, currentPage, refetch]
  );

  const handleRowChange = useCallback((rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);

  const getPaymentMethod = useCallback(
    (paymentId: string | undefined | null) => {
      if (paymentId) {
        const selectedPaymentMethod = availablePaymentMethods?.find((paymentMethod) => paymentMethod?.id === paymentId);
        if (selectedPaymentMethod) {
          if (selectedPaymentMethod?.type === PaymentMethodType.CARD) {
            const creditCard = selectedPaymentMethod as CreditCard;
            return <span>{`**** **** **** ${creditCard?.card?.last4Numbers ?? ''}`}</span>;
          } else if (selectedPaymentMethod?.type === PaymentMethodType.DEFERRED) {
            return <span>Payment on terms</span>;
          }
        }
      }
      return <NoDataContentInTable />;
    },
    [availablePaymentMethods]
  );

  const getColumnData = (visibleColumns: ColumnList[], invoice: Invoice) => {
    const {
      id,
      invoiceNumber,
      status,
      dueDate,
      grandTotal,
      paymentCurrency,
      createdDate,
      paymentMethod,
      shipTo,
      pdfLink,
      paymentMethodId,
      paidDate,
      sourceSystem,
    } = invoice || {};
    const rowData = visibleColumns.map((column) => {
      switch (column.name) {
        case 'Invoice number':
          return (
            <td>
              <commonTabs.InvoiceCell
                invoiceId={id}
                status={status}
                invoiceNumber={invoiceNumber || ''}
                paymentError={!!invoice?.dunningStatus?.length}
                pdfLink={pdfLink}
                transactionAccountId={transactionAccountId}
                grandTotal={grandTotal}
                sourceSystem={sourceSystem || ''}
                paymentMethod={invoice.paymentMethod as generatedTypesNew.InvoicePaymentMethod}
              />
            </td>
          );
        case 'Sold-to address':
          return <td>{getSoldToAddress(shipTo)}</td>;
        case 'Contact address':
          return <td>{getSoldToAddress(shipTo)}</td>;
        case 'Payment method':
          return <td>{getPaymentMethod(paymentMethodId)}</td>;
        case 'Due on':
          return (
            <td>
              {dueDate ? (
                <div
                  className={classNames({
                    'overdue-date': InvoiceUtil.isDueDateOverdue(
                      status ?? '',
                      !!invoice?.dunningStatus?.length,
                      dueDate
                    ),
                  })}
                >
                  <FormattedDate date={dueDate} format={NEW_DATE_FORMAT} />
                </div>
              ) : (
                <NoDataContentInTable />
              )}
            </td>
          );
        case 'Issued on':
          return (
            <td>
              {createdDate ? (
                <FormattedDate date={createdDate} showTime={true} format={NEW_DATE_TIME_FORMAT} />
              ) : (
                <NoDataContentInTable />
              )}
            </td>
          );
        case 'PO number':
          return (
            <td>{!isNil(invoice?.purchaseOrderNumber) ? invoice.purchaseOrderNumber : <NoDataContentInTable />}</td>
          );
        case 'Paid date':
          return (
            <td>
              {paidDate ? (
                <FormattedDate date={paidDate} showTime={true} format={NEW_DATE_TIME_FORMAT} />
              ) : (
                <NoDataContentInTable />
              )}
            </td>
          );
        case 'Amount':
          return (
            <td className="invoice-amount-cell">
              {!getIsFreeInvoice(paymentMethod as PaymentMethods) && grandTotal ? (
                <formattedV2.FormattedCurrencyV2 value={grandTotal} currency={paymentCurrency} showCurrencyCode />
              ) : (
                'Free'
              )}
            </td>
          );
      }
    });
    return rowData;
  };

  useEffect(() => {
    const filteredColumns = getFilteredColumns(selectedTabKey);
    setColumnList([...filteredColumns]);
  }, [location, selectedTabKey]);

  const getSelectedColumns = useCallback(
    (columnsList: ColumnList[]) => {
      let columns = [];
      return (columns = columnsList?.filter(
        (column) => column?.isChecked || !Object.prototype.hasOwnProperty.call(column, 'isChecked')
      ));
    },
    [columnsList, location]
  );

  const handleTabSelect = (tabLabel: string) => {
    setSelectedTabKey(kebabCase(tabLabel));
    history.push({
      hash: location?.hash,
      search: new URLSearchParams({ inner_tab: kebabCase(tabLabel) }).toString(),
    });
  };

  const props = {
    children: (
      <>
        <ColGroup columns={getSelectedColumns(columnsList)} />
        {page !== 'contacts' && <TableHead columns={getSelectedColumns(columnsList)} />}
        <TableBody>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/*@ts-ignore*/}
          {(nodes ?? []).map((invoice: Invoice, index: number) => {
            if (!invoice) return null;
            const { id, invoiceNumber, sourceSystem, refunds } = invoice;

            const identifier = getIdentifier({
              id,
              invoiceNumber,
              sourceSystem,
            });
            const rowPayload = {
              invoiceId: identifier,
            };

            return (
              <>
                <OpenSidebarRow
                  key={`invoice-table-row-${index}`}
                  sidebar={GloballyAllowedSidebar.INVOICE_PREVIEW_V2}
                  payload={rowPayload}
                  sidebarOptions={{
                    isOverlay: true,
                    sidebarWidth: '70%',
                  }}
                  isSelected={false}
                  data-testid={`invoice-table-row-${index}`}
                  className={classNames({
                    'grey-row': index % 2 !== 0,
                    'white-row': index % 2 === 0,
                  })}
                  hasDefaultClassName={false}
                >
                  {getColumnData(getSelectedColumns(columnsList), invoice).map((node) => node)}
                </OpenSidebarRow>
                <>
                  {!isEmpty(refunds) &&
                    //@ts-ignore
                    (refunds ?? []).map((ccpCreditNote: Refunds, indexRefund: number) => {
                      return (
                        <InvoiceChildRefundRow
                          key={`invoice-refund-row${indexRefund}`}
                          parentColumns={columnsList?.length}
                          nextColumns={1}
                          hideActions={false}
                          refund={ccpCreditNote}
                          isSelected={isSidebarOpen}
                          index={index}
                        />
                      );
                    })}
                </>
              </>
            );
          })}
        </TableBody>
        {selectedTabKey === 'hams-invoices' && !isEmpty(nodes) && (
          <Button
            className="load-more-button"
            onClick={handleLoadMore}
            testId="load-more-button"
            isDisabled={!lastEvaluatedKey}
          >
            Load More HAMS Invoices
          </Button>
        )}
      </>
    ),
    noResults: hasNoResults,
    isLoading: loading,
    error: error?.toString(),
    selectableResults: true,
    totalCount: totalCount,
    rowsPerPage: rowsPerPage,
    currentPage: currentPage,
    onPageChange: onPageChange,
    onRowChange: handleRowChange,
    testId: 'transaction-account-invoice-tab',
    emptyStateProps: {
      header: '',
      description: NO_DETAILS_TO_SHOW,
    },
    actions: pendingInvoiceData?.transactionAccount?.pendingInvoices
      ? [
          () => (
            <DismissableInlineMessage
              position="bottom-start"
              renderMessage={() => {
                return (
                  <>
                    <div className="generating-invoice-dialog-content">
                      We’re getting some invoices ready for this account.
                      <div>
                        See
                        <Link to={`${transactionAccountId}?inner_tab=billing-profiles#account-details`}>
                          {' '}
                          Account details {'>'} Billing profiles
                        </Link>{' '}
                        for more details.
                      </div>
                    </div>
                  </>
                );
              }}
              renderTitle={(toggleMessage) => (
                <div className="hoverable-icon tooltip-container" onClick={toggleMessage} data-testid="inline-message">
                  <Icon type="info" size="medium" /> <span className="tooltip-text">Generating invoices</span>
                </div>
              )}
            />
          ),
        ]
      : [],
  };

  return (
    <div className="transaction-account-invoice-tabs">
      {page !== 'contacts' ? (
        <>
          {' '}
          <h4 className={location.hash !== '#invoices' ? 'invoice-heading' : ''}>Invoices</h4>
          <PageTabs
            selectedTabKey={selectedTabKey}
            onTabSelect={handleTabSelect}
            className="invoice-tabs-container invoice-tab"
            showAllTab={false}
          >
            <TransactionAccountInvoiceTable
              columnsList={columnsList}
              label="CCP Invoices"
              props={props}
              page={page}
              setColumnList={setColumnList}
              totalCount={totalCount}
              isPaginationEnabled={totalCount > DEFAULT_ROWS_PER_PAGE}
            />
            <TransactionAccountInvoiceTable
              label="HAMS Invoices"
              props={props}
              page={page}
              totalCount={totalCount}
              setColumnList={setColumnList}
              columnsList={columnsList}
              isPaginationEnabled={false}
            />
          </PageTabs>
        </>
      ) : (
        <TransactionAccountInvoiceTable
          columnsList={columnsList}
          label="CCP Invoices"
          props={props}
          page={page}
          setColumnList={setColumnList}
          totalCount={totalCount}
          isPaginationEnabled={totalCount > DEFAULT_ROWS_PER_PAGE}
        />
      )}
    </div>
  );
});

export default InvoiceTabTxA;
